/**
 * Capacity rules — frontend helpers (pure), slot-based.
 *
 * A capacity rule occupies one SLOT: "Default paths" (the flow's default
 * bucket), "Rule paths" (a per-EACH-rule-group cap), or a whole layer
 * (landers/offers). One cap per slot. `max: null` = Ignore (unlimited).
 * For Media Devoted | Created By Ciro Autiero
 */

import {
  capacityBoundsIssue,
  slotsOfRule,
  type CapacityRule,
  type CapacitySlot,
  type FlowOptimizerItemPreview,
} from '@trafficpulse/shared';

export const SLOT_LABEL: Record<CapacitySlot, string> = {
  'paths-default': 'Default paths',
  'paths-rules': 'Rule paths',
  landers: 'Landers',
  offers: 'Offers',
};

/** Paths-focused launch: landers/offers slots stay gated ("· soon"). */
export const AVAILABLE_SLOTS: readonly CapacitySlot[] = [
  'paths-default',
  'paths-rules',
];
export const COMING_SOON_SLOTS: readonly CapacitySlot[] = ['landers', 'offers'];

let seq = 0;

/**
 * Fresh capacity rule occupying one slot, capped at 5 by default and with NO
 * floor — a min is opt-in, because setting one silently would change how an
 * existing flow rotates the moment the rule is created.
 */
export function makeCapacityRule(slot: CapacitySlot): CapacityRule {
  seq += 1;
  const id = `cap_${Date.now()}_${seq}`;
  if (slot === 'landers' || slot === 'offers') {
    return { id, name: '', layers: [slot], max: 5 };
  }
  return {
    id,
    name: '',
    layers: ['paths'],
    max: 5,
    ...(slot === 'paths-rules' ? { pathScope: 'rules' as const } : {}),
  };
}

/**
 * Why a min/max pair is not usable, or null when it is.
 *
 * Delegates to the shared rule rather than restating it — the API refuses the
 * same pair, and an editor that disagreed with the server about what is valid
 * is worse than no check at all.
 */
export function capacityBoundsError(rule: CapacityRule): string | null {
  return capacityBoundsIssue(rule);
}

/** The single slot a card edits (rules are authored one-slot-per-card). */
export function slotOfRule(rule: CapacityRule): CapacitySlot {
  return slotsOfRule(rule)[0] ?? 'paths-default';
}

/** Slots already covered by any rule (a cap is one-per-slot). */
export function coveredSlots(rules: CapacityRule[]): Set<CapacitySlot> {
  return new Set(rules.flatMap(slotsOfRule));
}

/** First available slot not yet covered, or null when all are taken. */
export function firstFreeSlot(rules: CapacityRule[]): CapacitySlot | null {
  const covered = coveredSlots(rules);
  return AVAILABLE_SLOTS.find((s) => !covered.has(s)) ?? null;
}

/** Slots claimed by OTHER rules (locked in this card's chips). */
export function slotsUsedByOthers(
  rules: CapacityRule[],
  ruleId: string,
): CapacitySlot[] {
  const used = coveredSlots(rules.filter((r) => r.id !== ruleId));
  return [...used];
}

export function updateRule(rules: CapacityRule[], next: CapacityRule): CapacityRule[] {
  return rules.map((r) => (r.id === next.id ? next : r));
}

export function removeRule(rules: CapacityRule[], id: string): CapacityRule[] {
  return rules.filter((r) => r.id !== id);
}

type PreviewItem = Pick<FlowOptimizerItemPreview, 'layer' | 'active' | 'parentId'>;

export interface SlotActiveCounts {
  bySlot: Record<CapacitySlot, number>;
  /** Rule-paths breakdown: preview pool id (`flow:group:<id>`) → active count. */
  byRuleGroup: Map<string, number>;
}

/** ACTIVE counts per slot from the live-preview items (paths split by pool). */
export function activeCountBySlot(
  items: ReadonlyArray<PreviewItem>,
): SlotActiveCounts {
  const bySlot: Record<CapacitySlot, number> = {
    'paths-default': 0,
    'paths-rules': 0,
    landers: 0,
    offers: 0,
  };
  const byRuleGroup = new Map<string, number>();
  for (const it of items) {
    if (!it.active) continue;
    if (it.layer !== 'paths') {
      bySlot[it.layer] += 1;
    } else if (!it.parentId || it.parentId === 'flow:default') {
      bySlot['paths-default'] += 1;
    } else {
      bySlot['paths-rules'] += 1;
      byRuleGroup.set(it.parentId, (byRuleGroup.get(it.parentId) ?? 0) + 1);
    }
  }
  return { bySlot, byRuleGroup };
}
